import { useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound, Loader2, Plus } from "lucide-react";
import { ModulePanel } from "@/components/admin/AdminPrimitives";
import { useClesApi, useCreerCleApi, useRevoquerCleApi } from "@/hooks/useClesApi";
import type { CleApiDto } from "@/types/cle-api.types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Seules permissions existantes : aucune ne permet de publier, supprimer ou administrer.
const SCOPES = [
  { value: "articles:create", label: "Créer des brouillons" },
  { value: "articles:read", label: "Lire ses articles et les référentiels" },
  { value: "articles:update", label: "Modifier ses brouillons" },
  { value: "articles:submit", label: "Soumettre à validation humaine" },
  { value: "media:create", label: "Téléverser des illustrations" },
];

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—";

/** Clés d'accès de la newsroom IA (ChatGPT) à l'API éditoriale /api/v1/newsroom. */
export function ClesApiPanel() {
  const { data, isLoading } = useClesApi();
  const creer = useCreerCleApi();
  const revoquer = useRevoquerCleApi();

  const [nom, setNom] = useState("");
  const [scopes, setScopes] = useState<string[]>(SCOPES.map((s) => s.value));
  const [expireLe, setExpireLe] = useState("");
  const [cleCreee, setCleCreee] = useState<CleApiDto | null>(null);
  const [aRevoquer, setARevoquer] = useState<CleApiDto | null>(null);

  const cles = Array.isArray(data) ? data : [];

  const soumettre = () => {
    if (!nom.trim() || scopes.length === 0) return;
    creer.mutate(
      {
        nom: nom.trim(),
        scopes,
        expireLe: expireLe ? new Date(`${expireLe}T23:59:59`).toISOString() : null,
      },
      {
        onSuccess: (cle) => {
          setCleCreee(cle);
          setNom("");
          setExpireLe("");
          setScopes(SCOPES.map((s) => s.value));
        },
        onError: (e: any) => toast.error(e?.response?.data?.message || "Création de la clé impossible"),
      },
    );
  };

  const copier = async (valeur: string) => {
    try {
      await navigator.clipboard.writeText(valeur);
      toast.success("Clé copiée");
    } catch {
      toast.error("Copie impossible : sélectionnez la clé manuellement");
    }
  };

  return (
    <ModulePanel
      title="Clés API — newsroom IA"
      description="Accès de la newsroom IA à l'API éditoriale : dépôt de brouillons et soumission à validation. Une clé ne permet jamais de publier, supprimer ni administrer."
    >
      <div className="space-y-6">
        {cleCreee?.cle && (
          <div className="rounded-md border border-verified/40 bg-verified/5 p-4 space-y-2">
            <p className="text-sm font-semibold text-foreground">Clé « {cleCreee.nom} » créée</p>
            <p className="text-xs text-muted-foreground">
              Copiez-la maintenant et transmettez-la par un canal sûr (gestionnaire de secrets) : elle ne sera plus
              jamais affichée.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 overflow-x-auto rounded bg-background px-3 py-2 text-xs">{cleCreee.cle}</code>
              <button
                type="button"
                onClick={() => copier(cleCreee.cle!)}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-semibold"
              >
                <Copy className="h-3.5 w-3.5" /> Copier
              </button>
            </div>
            <button type="button" onClick={() => setCleCreee(null)} className="text-xs text-muted-foreground underline">
              J'ai copié la clé
            </button>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Nom</span>
            <input
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              placeholder="Ex. ChatGPT newsroom — production"
              maxLength={100}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Expiration (optionnelle)
            </span>
            <input
              type="date"
              value={expireLe}
              onChange={(e) => setExpireLe(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
        </div>
        <fieldset className="space-y-1.5">
          <legend className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Permissions</legend>
          {SCOPES.map((s) => (
            <label key={s.value} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={scopes.includes(s.value)}
                onChange={(e) =>
                  setScopes((prev) => (e.target.checked ? [...prev, s.value] : prev.filter((x) => x !== s.value)))
                }
                className="h-4 w-4 rounded border-border"
              />
              <span>{s.label}</span>
              <code className="text-[10px] text-muted-foreground">{s.value}</code>
            </label>
          ))}
        </fieldset>
        <button
          type="button"
          onClick={soumettre}
          disabled={!nom.trim() || scopes.length === 0 || creer.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-xs font-semibold text-background disabled:opacity-60"
        >
          {creer.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Générer une clé
        </button>

        <div className="overflow-x-auto">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Chargement…</p>
          ) : cles.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <KeyRound className="h-4 w-4" /> Aucune clé pour l'instant.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2">Nom</th>
                  <th className="px-3 py-2">Clé</th>
                  <th className="px-3 py-2">Permissions</th>
                  <th className="px-3 py-2">Dernière utilisation</th>
                  <th className="px-3 py-2">État</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {cles.map((c) => (
                  <tr key={c.id} className="border-b border-rule last:border-0">
                    <td className="px-3 py-2.5 font-medium">{c.nom}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{c.prefixe}…</td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">{c.scopes.join(", ")}</td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                      {fmtDate(c.derniereUtilisation)}
                    </td>
                    <td className="px-3 py-2.5 text-xs whitespace-nowrap">
                      {c.active ? (
                        <span className="text-verified">Active{c.expireLe ? ` · expire ${fmtDate(c.expireLe)}` : ""}</span>
                      ) : c.revoqueeLe ? (
                        <span className="text-alert">Révoquée {fmtDate(c.revoqueeLe)}</span>
                      ) : (
                        <span className="text-muted-foreground">Expirée</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {c.active && (
                        <button
                          type="button"
                          onClick={() => setARevoquer(c)}
                          className="rounded-md border border-alert px-2.5 py-1 text-xs font-semibold text-alert hover:bg-alert/10"
                        >
                          Révoquer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <AlertDialog open={!!aRevoquer} onOpenChange={(open) => !open && setARevoquer(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Révoquer « {aRevoquer?.nom} » ?</AlertDialogTitle>
            <AlertDialogDescription>
              La newsroom ne pourra plus utiliser cette clé, avec effet immédiat. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-alert text-alert-foreground hover:bg-alert/90"
              disabled={revoquer.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!aRevoquer) return;
                revoquer.mutate(aRevoquer.id, {
                  onSuccess: () => {
                    toast.success("Clé révoquée");
                    setARevoquer(null);
                  },
                  onError: () => toast.error("Révocation impossible"),
                });
              }}
            >
              {revoquer.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Révoquer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ModulePanel>
  );
}
